import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ReportContractsState } from './types';
import { AppState } from 'src/store/store';
import { GetReportContractsVM } from 'src/api/contracts';
import reportContractsApiService from 'src/api/services/reportContractsApiService';
import { selectUserInfo } from 'src/userInfo/slice';

const initialState: ReportContractsState = {
    status: 'initial',
    year: undefined,
    airlineCode: undefined,
    items: [],
    requestId: undefined
};

export const loadDataSIC = createAsyncThunk<GetReportContractsVM[], void, { state: AppState }>(
    'report-contracts/data-sic',
    async (_, { getState }) => {
        const state = getState();
        const year = selectReportContractsYear(state);
        const airlineCode = selectReportContractsAirlineCode(state);
        return await reportContractsApiService.loadReportContracts(year!, airlineCode!);
    }
);

export const loadDataAC = createAsyncThunk<GetReportContractsVM[], void, { state: AppState }>(
    'report-contracts/data-ac',
    async (_, { getState }) => {
        const state = getState();
        const year = selectReportContractsYear(state);
        const airlineCode = selectUserInfo(state)?.airlineCode;
        return await reportContractsApiService.loadReportContracts(year!, airlineCode!);
    }
);

const slice = createSlice({
    name: 'report-contracts',
    initialState,
    reducers: {
        yearChanged: (state, action: PayloadAction<number | undefined>) => {
            state.year = action.payload;
            state.items = [];
        },
        airlineCodeChanged: (state, action: PayloadAction<string>) => {
            state.airlineCode = action.payload;
            state.items = [];
        },
        reset: () => initialState
    },
    extraReducers(builder) {
        builder
            .addCase(loadDataSIC.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(loadDataSIC.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.map((itemDto, index) => ({ ...itemDto, key: index }));
                }
            })
            .addCase(loadDataSIC.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                }
            })
            .addCase(loadDataAC.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
            })
            .addCase(loadDataAC.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.map((itemDto, index) => ({ ...itemDto, key: index }));
                }
            })
            .addCase(loadDataAC.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                }
            });
    }
});

export const { yearChanged, airlineCodeChanged, reset } = slice.actions;

const selectReportContracts = (state: AppState) => state.reportContracts;

export const selectReportContractsYear = (state: AppState) => selectReportContracts(state).year;

export const selectReportContractsAirlineCode = (state: AppState) => selectReportContracts(state).airlineCode;

export const selectReportContractsStatus = (state: AppState) => selectReportContracts(state).status;

export const selectReportContractsItems = (state: AppState) => selectReportContracts(state).items;

export default slice.reducer;
